import React, { useState, useEffect } from 'react';
import { Plus, FileText } from "lucide-react";
import { AddDesign } from "../../client_contact_stages/verve-design/components/AddDesign";
import { designService } from "../../client_contact_stages/verve-design/services/designService";
import API, { BASE_URL } from "@/lib/API";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import toast from 'react-hot-toast';

import { LeadExecutionTable } from './lead_ececution_table';
import { LeadExecutionDocumentViewer } from './LeadExecutionDocumentViewer';
import { ActionView, ActionDownload, ActionApprove, ActionReject, ActionDelete } from "@/components/ui/LatestTableActions";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";
import { useAuthStore } from '@/store/authStore';

export const SignOffCopyTab = ({ project, onUpdate }) => {
  const [designs, setDesigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // View Modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Delete / Re-upload Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAndReupload, setDeleteAndReupload] = useState(false);

  const { user } = useAuthStore();
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';

  const fetchDesigns = async () => {
    try {
      setIsLoading(true);
      const res = await API.get(`/lead-executions-designs/${project._id}/designs`);
      setDesigns(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching designs:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project?._id) {
      fetchDesigns();
    }
  }, [project?._id]);

  const handleUploadSubmit = async (formDataArray) => {
    try {
      if (formDataArray && formDataArray.length > 0) {
        for (const formData of formDataArray) {
          await designService.uploadMedia(formData);
        }
        showSuccessMessage("Sign Off Copy uploaded successfully");
      }
      setIsAddModalOpen(false);
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleView = (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    setViewerFile({ url, name: file.filename || file.name });
    setViewerOpen(true);
  };

  const handleApprove = async (file) => {
    try {
      await API.patch(`/design/${file.designId}/${file.fileId}/approve`);
      showSuccessMessage("Sign Off Copy approved successfully");
      fetchDesigns();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleReject = (file) => {
    setFileToDelete(file);
    setDeleteAndReupload(false);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteAndReupload = (file) => {
    setFileToDelete(file);
    setDeleteAndReupload(true);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setIsDeleting(true);
    try {
      await designService.deleteDesignMedia(fileToDelete.designId, fileToDelete.fileId);
      showSuccessMessage(
        deleteAndReupload
          ? "Sign Off Copy removed. You can now re-upload."
          : "Sign Off Copy rejected and deleted successfully"
      );
      fetchDesigns();
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
      if (deleteAndReupload) {
        setIsAddModalOpen(true);
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setIsDeleting(false);
      setDeleteAndReupload(false);
    }
  };

  const handleDownload = async (file) => {
    const url = file.url || `${BASE_URL}/api/design/media/${file.fileId}/${file.filename}`;
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('blum_token')}`
        }
      });
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename || file.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download file");
    }
  };

  const columns = [
    {
      header: "File Name",
      render: (file) => (
        <div
          className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
          onClick={() => handleView(file)}
        >
          <FileText className="w-4 h-4 text-slate-400" />
          <span className="font-semibold text-[#65768e] text-[11px] truncate max-w-[200px]" title={file.filename || file.name}>
            {file.filename || file.name}
          </span>
        </div>
      )
    },
    {
      header: "Stage",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold capitalize">
          {(file.stage || 'other').replace(/_/g, ' ')}
        </span>
      )
    },
    {
      header: "Status",
      render: (file) => (
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${file.isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          {file.isApproved ? 'Approved' : 'Pending Review'}
        </span>
      )
    },
    {
      header: "Size",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          {file.size ? (file.size / 1024 / 1024).toFixed(2) : '0'} MB
        </span>
      )
    },
    {
      header: "Uploaded By",
      render: (file) => (
        <div className="flex flex-col">
          <span className="text-[#65768e] text-[11px] font-semibold">{file.uploadedBy?.name || '—'}</span>
          {file.uploadedBy?.role && (
            <span className="text-slate-400 text-[9px] uppercase tracking-wider font-bold mt-0.5">
              {file.uploadedBy.role.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )
    },
    {
      header: "Date",
      render: (file) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          {new Date(file.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      )
    },
    {
      header: "Actions",
      render: (file) => (
        <div className="flex items-center gap-1.5">
          <ActionView onClick={() => handleView(file)} title="View" />
          <ActionDownload onClick={() => handleDownload(file)} title="Download" />
          {isOwnerOrAdmin && !file.isApproved && (
            <ActionApprove onClick={() => handleApprove(file)} title="Approve" />
          )}
          {isOwnerOrAdmin && !file.isApproved && (
            <ActionReject onClick={() => handleReject(file)} title="Reject & Delete" />
          )}
          {isOwnerOrAdmin && (
            <ActionDelete onClick={() => handleDeleteAndReupload(file)} title="Delete & Re-upload" />
          )}
        </div>
      )
    }
  ];

  return (
    <div className="w-full flex flex-col animate-in fade-in duration-500">
      <div className="flex justify-end items-center px-1 mb-2">
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-[11px] font-bold tracking-wide uppercase border border-[#2b448f] text-[#2b448f] bg-white rounded-md hover:bg-slate-50 transition-colors shadow-sm ml-auto"
        >
          <Plus className="w-3.5 h-3.5" />
          Upload Sign Off Copy
        </button>
      </div>

      <LeadExecutionTable
        columns={columns}
        data={designs.filter(d => d.stage === 'sign_of_copy')}
        isLoading={isLoading}
      />

      <AddDesign
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleUploadSubmit}
        initialData={project.leadId || project.leadDetails}
        targetStatus="sign_of_copy"
        titleOverride="Upload Sign Off Copy"
      />

      <LeadExecutionDocumentViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFileToDelete(null);
          setDeleteAndReupload(false);
        }}
        onConfirm={confirmDelete}
        title={deleteAndReupload ? "Delete & Re-upload Sign Off Copy" : "Reject Sign Off Copy"}
        message={
          deleteAndReupload
            ? "This will permanently delete the file from Google Drive and open the upload form for a replacement."
            : "Are you sure you want to reject and delete this Sign Off Copy? This will permanently remove the file from Google Drive."
        }
        confirmLabel={deleteAndReupload ? "Delete & Re-upload" : "Reject & Delete"}
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SignOffCopyTab;
